import { useEffect, useRef, useState } from "react";
import { api, type BillingState } from "./api";

const INTERVAL_MS = 2_500;
const TIMEOUT_MS = 120_000;

export type BillingPollStatus = "polling" | "success" | "timeout";

// Polls GET /v1/admin/billing until the entitlement leaves the 'locked'
// read model (the LS webhook has landed and activated the entitlement)
// or ``TIMEOUT_MS`` elapses — the "Finishing setup…" step after a
// checkout redirect back to ``?checkout=return`` (A6). Shared by
// Welcome (initial trial checkout) and Billing (plan-switch checkout).
//
// ``active`` gates the whole effect so a caller can mount the hook
// unconditionally and only start polling once it knows it's on a
// checkout-return leg (e.g. from a query param).
export function useBillingPoll(active: boolean): {
  status: BillingPollStatus;
  state: BillingState | null;
} {
  const [status, setStatus] = useState<BillingPollStatus>("polling");
  const [state, setState] = useState<BillingState | null>(null);
  // Re-entrant guard: StrictMode double-invokes effects in dev, and the
  // interval callback captures `cancelled` via closure below anyway, but
  // keeping the start time in a ref avoids resetting the timeout budget
  // if `active` flips more than once.
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    if (startedAtRef.current == null) startedAtRef.current = Date.now();
    const startedAt = startedAtRef.current;

    let timer: ReturnType<typeof setTimeout> | undefined;

    async function tick() {
      try {
        const s = await api.billing();
        if (cancelled) return;
        setState(s);
        if (s.entitlement && s.entitlement.read_model !== "locked") {
          setStatus("success");
          return;
        }
      } catch {
        // Transient fetch failure — keep polling until the timeout budget
        // runs out rather than failing the whole "finishing setup" step.
      }
      if (cancelled) return;
      if (Date.now() - startedAt >= TIMEOUT_MS) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(tick, INTERVAL_MS);
    }

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active]);

  return { status, state };
}
